import { IconAlertTriangle, IconClock, IconEyeOff, IconX } from '@tabler/icons-react'

/**
 * A node-and-connector flow with at most one metric per edge.
 *
 * Doc 02 §6.2 names this primitive; doc 01 §3.2 is its first consumer, the
 * Pipeline View, which draws one entity run as a funnel. It is deliberately a
 * LINE, not a graph layout: nodes are drawn in the order given, a main edge
 * joins two adjacent nodes, and a side edge (one with no `to`) leaves its node
 * for somewhere off the line, such as rows rejected at a transform. An edge
 * between nodes that are not adjacent is not drawn.
 *
 * The primitive formats nothing and decides nothing. Every value arrives as a
 * finished string, so a measured zero reaches it as "0" and an absent reading
 * as NOT_MEASURED, and the two can never be confused here. Every tone is the
 * caller's.
 *
 * Styling: tokens only, from a stylesheet this component carries itself
 * (React 19 hoists it into <head> once, however many flows are mounted), so the
 * primitive owns its layout. State sits in data attributes, which a Mantine
 * `styles` prop cannot target (tokens.css, GOTCHA). Status is carried by an
 * icon and words as well as colour, never by colour alone, as in StageStrip.
 *
 * Narrow containers: below 600px of its own width the flow turns vertical
 * rather than wrapping. A sequence that wraps mid-line reads as two sequences
 * and misleads about order (the StageStrip note, shell.css). Wider than that
 * but too narrow for every node, the line scrolls inside its own container.
 */

/**
 * `neutral` is plain ink. `unobserved` is also neutral, but dashed: nothing
 * measured it, and it must not look like a pass. `active` is work in progress.
 */
export type FlowTone = 'neutral' | 'active' | 'warning' | 'critical' | 'unobserved'

/** One reading: an already formatted value and what it counts. */
export interface FlowFigure {
  /** Finished text, such as exactNumber(0) === '0', or NOT_MEASURED when nothing measured it. */
  value: string
  label: string
  tone?: FlowTone
}

export interface FlowNode {
  id: string
  label: string
  figures: FlowFigure[]
  /** A short state in words beside the label, such as 'Not measured'. It carries the tone in text. */
  status?: string
  /** One line under the figures. */
  caption?: string
  tone?: FlowTone
}

export interface FlowEdge {
  from: string
  /** The next node on the line. Omit it for a side edge: whatever leaves the flow at `from`. */
  to?: string
  /** At most one per edge. An edge with nothing measured to carry is drawn bare, not labelled with a guess. */
  metric?: FlowFigure
}

export interface PipelineFlowProps {
  nodes: FlowNode[]
  edges: FlowEdge[]
  /** The diagram's accessible name. */
  label: string
}

function ToneIcon({ tone }: { tone: FlowTone | undefined }) {
  switch (tone) {
    case 'warning': return <IconAlertTriangle className="lh-pflow-icon" size={12} stroke={2.5} aria-hidden="true" />
    case 'critical': return <IconX className="lh-pflow-icon" size={12} stroke={3} aria-hidden="true" />
    case 'active': return <IconClock className="lh-pflow-icon" size={12} stroke={2.5} aria-hidden="true" />
    case 'unobserved': return <IconEyeOff className="lh-pflow-icon" size={12} stroke={2} aria-hidden="true" />
    default: return null
  }
}

/** Points right. The stylesheet turns it down wherever the line runs down. */
function ArrowHead() {
  return (
    <svg className="lh-pflow-head" width="8" height="10" viewBox="0 0 8 10" aria-hidden="true" focusable="false">
      <path d="M0 0L8 5L0 10Z" fill="currentColor" />
    </svg>
  )
}

function Figure({ figure }: { figure: FlowFigure }) {
  const tone = figure.tone ?? 'neutral'
  return (
    <span className="lh-pflow-figure" data-tone={tone}>
      <ToneIcon tone={tone} />
      <span className="lh-pflow-figure-value">{figure.value}</span>{' '}
      <span className="lh-pflow-figure-label">{figure.label}</span>
    </span>
  )
}

function Node({ node }: { node: FlowNode }) {
  const tone = node.tone ?? 'neutral'
  return (
    <div className="lh-pflow-node" data-node={node.id} data-tone={tone}>
      <span className="lh-pflow-node-head">
        <span className="lh-pflow-node-label">{node.label}</span>
        {node.status && (
          <span className="lh-pflow-node-status">
            <ToneIcon tone={tone} />
            {node.status}
          </span>
        )}
      </span>
      {node.figures.map((figure) => <Figure key={figure.label} figure={figure} />)}
      {node.caption && <span className="lh-pflow-caption">{node.caption}</span>}
    </div>
  )
}

export function PipelineFlow({ nodes, edges, label }: PipelineFlowProps) {
  const main = new Map<string, FlowEdge>()
  const side = new Map<string, FlowEdge[]>()
  for (const edge of edges) {
    if (edge.to === undefined) {
      side.set(edge.from, [...(side.get(edge.from) ?? []), edge])
    } else {
      main.set(`${edge.from}\u0000${edge.to}`, edge)
    }
  }

  return (
    <div className="lh-pflow">
      <style href="lh-pipeline-flow" precedence="default">{PIPELINE_FLOW_CSS}</style>
      <ol className="lh-pflow-track" role="list" aria-label={label}>
        {nodes.map((node, i) => {
          const next = nodes[i + 1]
          const edge = next ? main.get(`${node.id}\u0000${next.id}`) : undefined
          return (
            <li className="lh-pflow-step" key={node.id}>
              <div className="lh-pflow-station">
                <Node node={node} />
                {(side.get(node.id) ?? []).map((branch, b) => (
                  <div
                    className="lh-pflow-branch"
                    key={branch.metric?.label ?? b}
                    data-edge={`${node.id}:out`}
                    data-tone={branch.metric?.tone ?? 'neutral'}
                  >
                    <span className="lh-pflow-connector" aria-hidden="true">
                      <span className="lh-pflow-line" />
                      <ArrowHead />
                    </span>
                    {branch.metric && <Figure figure={branch.metric} />}
                  </div>
                ))}
              </div>
              {next && (
                <div
                  className="lh-pflow-edge"
                  data-edge={`${node.id}:${next.id}`}
                  data-tone={edge?.metric?.tone ?? 'neutral'}
                >
                  <span className="lh-pflow-connector" aria-hidden="true">
                    <span className="lh-pflow-line" />
                    <ArrowHead />
                  </span>
                  {edge?.metric && <Figure figure={edge.metric} />}
                  <span className="lh-visually-hidden">then</span>
                </div>
              )}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

/**
 * The flow's own stylesheet. Tokens only (tokens.css): no literal colour, so it
 * follows both themes. Horizontal by default; vertical below 600px of the
 * flow's own width, which covers every phone, because a container is never
 * wider than the viewport.
 */
const PIPELINE_FLOW_CSS = `
.lh-pflow {
  container-type: inline-size;
  container-name: lh-pflow;
  min-width: 0;
  font-family: var(--font-ui);
}
.lh-pflow-track {
  list-style: none;
  margin: 0;
  padding: 2px 0 4px;
  display: flex;
  align-items: flex-start;
  overflow-x: auto;
  scrollbar-width: thin;
}
/* No min-width: 0 here. A step must not shrink below its node and edge, or
   they overlap the next step instead of making the track scroll. */
.lh-pflow-step {
  display: flex;
  align-items: flex-start;
  flex: 1 1 auto;
}
.lh-pflow-step:last-child { flex: 0 1 auto; }
.lh-pflow-station {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: none;
  width: 136px;
}
.lh-pflow-node {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background-color: var(--color-bg-card);
  color: var(--color-text-primary);
  min-width: 0;
}
.lh-pflow-node[data-tone='active'] { border-color: var(--stage-active-track); }
.lh-pflow-node[data-tone='warning'] { border-color: var(--status-warning-border); }
.lh-pflow-node[data-tone='critical'] { border-color: var(--status-critical-border); }
.lh-pflow-node[data-tone='unobserved'] {
  border-style: dashed;
  background-color: transparent;
  color: var(--color-text-secondary);
}
.lh-pflow-node-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 2px 6px;
}
.lh-pflow-node-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
}
.lh-pflow-node-status {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 500;
  color: var(--color-text-secondary);
}
.lh-pflow-node[data-tone='active'] .lh-pflow-node-status { color: var(--stage-active-fg); }
.lh-pflow-figure {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 4px;
  line-height: 1.25;
}
.lh-pflow-figure-value {
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.lh-pflow-figure-label {
  font-size: 11px;
  color: var(--color-text-secondary);
}
.lh-pflow-figure[data-tone='warning'] .lh-pflow-icon { color: var(--status-warning-fg); }
.lh-pflow-figure[data-tone='critical'] .lh-pflow-icon { color: var(--status-critical-fg); }
.lh-pflow-icon { flex: none; align-self: center; }
.lh-pflow-caption {
  font-size: 10.5px;
  line-height: 1.3;
  color: var(--color-text-secondary);
}
.lh-pflow-edge {
  flex: 1 1 64px;
  min-width: 56px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  padding: 14px 4px 0;
  text-align: center;
}
.lh-pflow-edge .lh-pflow-figure { justify-content: center; }
.lh-pflow-edge .lh-pflow-figure-value { font-size: 12px; }
.lh-pflow-connector {
  display: flex;
  align-items: center;
  color: var(--stage-pending-track);
}
.lh-pflow-line {
  flex: 1 1 auto;
  height: 0;
  border-top: 2px solid currentColor;
}
.lh-pflow-head { flex: none; display: block; }
.lh-pflow-edge[data-tone='warning'] .lh-pflow-connector,
.lh-pflow-branch[data-tone='warning'] .lh-pflow-connector { color: var(--stage-warning-track); }
.lh-pflow-edge[data-tone='critical'] .lh-pflow-connector,
.lh-pflow-branch[data-tone='critical'] .lh-pflow-connector { color: var(--stage-failed-track); }
.lh-pflow-branch {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  text-align: center;
}
.lh-pflow-branch .lh-pflow-connector {
  flex-direction: column;
  height: 18px;
}
.lh-pflow-branch .lh-pflow-line {
  width: 0;
  height: auto;
  border-top: 0;
  border-left: 2px solid currentColor;
}
.lh-pflow-branch .lh-pflow-head { transform: rotate(90deg); }
.lh-pflow-branch .lh-pflow-figure { justify-content: center; }

@container lh-pflow (width < 600px) {
  .lh-pflow-track {
    flex-direction: column;
    align-items: stretch;
    overflow-x: visible;
  }
  .lh-pflow-step,
  .lh-pflow-step:last-child {
    flex-direction: column;
    align-items: stretch;
    flex: none;
  }
  .lh-pflow-station { width: auto; }
  .lh-pflow-branch {
    flex-direction: row;
    align-items: center;
    gap: 6px;
    padding: 4px 0 0 20px;
    text-align: left;
  }
  .lh-pflow-branch .lh-pflow-connector {
    flex-direction: row;
    height: auto;
    width: 20px;
  }
  .lh-pflow-branch .lh-pflow-line {
    width: auto;
    height: 0;
    border-left: 0;
    border-top: 2px solid currentColor;
  }
  .lh-pflow-branch .lh-pflow-head { transform: none; }
  .lh-pflow-edge {
    flex: none;
    min-width: 0;
    min-height: 34px;
    flex-direction: row;
    align-items: stretch;
    gap: 8px;
    padding: 2px 0 2px 20px;
    text-align: left;
  }
  .lh-pflow-edge .lh-pflow-connector {
    flex-direction: column;
    width: 10px;
  }
  .lh-pflow-edge .lh-pflow-line {
    width: 0;
    height: auto;
    border-top: 0;
    border-left: 2px solid currentColor;
  }
  .lh-pflow-edge .lh-pflow-head { transform: rotate(90deg); }
  .lh-pflow-edge .lh-pflow-figure { justify-content: flex-start; align-self: center; }
}
`
